import Vue from "vue";
import { mapGetters } from "vuex";
import { formatDateTime, stringHelper } from "@/components/mixins";
import { PartDetailsLink } from "@/components";
import { PartValidationReason } from "@/types";
import { EventTrackingService} from "@/services";
import { FontAwesomeIcon } from "@fortawesome/vue-fontawesome"
import { faPhoneSquareAlt } from "@fortawesome/free-solid-svg-icons";
import { default as RequestCurrentPricing } from "@/components/PartDetail/Dialogs/RequestCurrentPricing/RequestCurrentPricing.vue"

export default Vue.extend({
    name: "PartStatusMessage",
    components: {
        PartDetailsLink,
        FontAwesomeIcon,
        RequestCurrentPricing
    },
    mixins: [formatDateTime, stringHelper],
    computed: {
        ...mapGetters(["getContentItem","tenantDetails"]),
    },
    props: {
        details: {
            type: Object,
            required: true,
        },
        addToPartList: Function,
        showForms: {
            type: Boolean,
            default: true
        }
    },
    data: () => ({
        phoneIcon: faPhoneSquareAlt,
    }),
    created() {
        var action = ""
        var label = "";
          
        if (this.shouldShowReplacementAvailable(this.details)) {
            action = `Not-Orderable-Replacement ${this.reason}`;
            label = `${this.oemPartCode}->${this.replacementOemPartCode}`;
        }
        else if (this.shouldShowNoReplacementAvailable(this.details)) {
            action = `Not-Orderable-No-Replacement ${this.reason}`;
            label = this.oemPartCode;
        }
        else if (this.shouldShowItemNotPriced(this.details)) {
            action = `Request Price Availability ${this.reason}`;
            label = this.oemPartCode;
        }
        else if (this.shouldShowNotAuthorized(this.details)){
            action = "Channel Not Authorized";
            label = this.oemPartCode;
        }
        else if (this.shouldShowItemProhibited(this.details)) {
            action = "Item Prohibited";
            label = this.oemPartCode;
        }

        EventTrackingService.trackEvent({
            category: "Viewed Part Status",
            action: action,
            label: label,
            value: 1,
        });

    },
    methods: {
        getLocalizedMessageHtml(model: any) {
            switch (model.PartStatusMessage.PartValidationReason) {
                case PartValidationReason.IsHardFrozen:
                case PartValidationReason.IsSoftFrozen:
                case PartValidationReason.IsMarkedDoNotSell:
                case PartValidationReason.IsSuperseded:
                case PartValidationReason.IsCanceled:
                case PartValidationReason.IsDiscontinued:
                case PartValidationReason.MaoSoftFrozen:
                    var longMessage = this.getContentItem(model.PartStatusMessage.LongMessage);
                    return this.format(longMessage, this.tenantDetails.SupportPhoneNumber);
                default:
                    return this.getContentItem(model.PartStatusMessage.LongMessage);
            }
        },

        shouldShowNotAuthorized(model: any): boolean {
            return model.PartStatusMessage.PartValidationReason === PartValidationReason.IsChannelNotAuthorized;
        },

        shouldShowItemProhibited(model: any): boolean {
            return model.PartStatusMessage.PartValidationReason === PartValidationReason.IsProhibited;
        },

        shouldShowItemNotPriced(model: any): boolean {
            switch (model.PartStatusMessage.PartValidationReason) {
                case PartValidationReason.ItemNotPriced:
                    return true;
                case (PartValidationReason.MaoSoftFrozen):
                    if(model.DealerNetPrice === 0){
                        return true;
                    }
                    return false;
                default:
                    return false;
            }
        },

        shouldShowNonCancelable(model: any): boolean {                 
            if(this.details.PicCode.charAt(0) == "Y") {
                return true; 
           }

           return false;
        },

        shouldShowMao(model: any): boolean {
            switch (model.PartStatusMessage.PartValidationReason) {
                case PartValidationReason.IsMaoNoPrice:
                case PartValidationReason.IsMao:
                case PartValidationReason.IsMaoLimitExceeded:
                case PartValidationReason.IsMaoLimitExceededPriceNotCurrent:
                case PartValidationReason.IsMaoLimitExceededPriceNotCurrentNoStockQuantity:
                case PartValidationReason.IsMaoLimitExceededNoStockQuantity:
                case PartValidationReason.MaoSoftFrozen:
                    return true;
                default:
                    return false;
            }
        },

        shouldShowCantShipAir(model: any): boolean {
            return model.IsHazardous;
        },

        shouldShowNoReplacementAvailable(model: any): boolean {
            switch (model.PartStatusMessage.PartValidationReason) {
                case PartValidationReason.IsHardFrozen:
                case PartValidationReason.IsSoftFrozen:
                case PartValidationReason.IsMarkedDoNotSell:
                case PartValidationReason.MaoSoftFrozen:
                    return true;
                case PartValidationReason.IsSuperseded:
                    return model.PartStatusMessage.ReplacementPartOemPartCode ? false : true;
                case PartValidationReason.IsCanceled:
                case PartValidationReason.IsDiscontinued:
                    return model.PartStatusMessage.ReplacementPartsCount === 0;
                default:
                    return false;
            }
        },

        shouldShowReplacementAvailable(model: any): boolean {
            switch (model.PartStatusMessage.PartValidationReason) {
                case PartValidationReason.IsSuperseded:
                    return model.PartStatusMessage.ReplacementPartsCount > 0;
                case PartValidationReason.IsCanceled:
                case PartValidationReason.IsDiscontinued:
                    return model.PartStatusMessage.ReplacementPartsCount > 0;
                default:
                    return false;
            }
        },
        shouldShowRequestCurrentPricingButton(model: any): boolean {
            switch (model.PartStatusMessage.PartValidationReason) {
                case PartValidationReason.IsMaoLimitExceededPriceNotCurrentNoStockQuantity:
                case PartValidationReason.IsMaoLimitExceededPriceNotCurrent:
                case PartValidationReason.IsMaoNoPrice:
                    return true;
                default:
                    return false;
            }
        },
        shouldShowVendorSuppliedCamso(model: any): boolean{
            return model.PartStatusMessage.PartValidationReason === "VendorSuppliedCamso";
        },
        shouldShowVendorSuppliedInterstate(model: any): boolean{
            return model.PartStatusMessage.PartValidationReason === "VendorSuppliedInterstate";
        },
        showSupersessionTab() {
            var elements = document.getElementsByClassName("js-part-details-supersessions-tab");
            if(elements)
            {
                var supersessionTabLink = elements[0];        
                if(supersessionTabLink){
                    supersessionTabLink.click();
                    supersessionTabLink.scrollIntoView();
                    window.scrollBy(0, -100); // Scroll 100px up
                }
            }
            return
        },
        toggleRequestCurrentPricingForm(){
            //toggle with vanilla JS, so that form can be in separate instance of this component
            var requestCurrentPricingForm = document.getElementById("js-request-current-pricing-component");
            if(requestCurrentPricingForm){
                // If the element is visible, hide it
                if (window.getComputedStyle(requestCurrentPricingForm).display === 'block') {
                    requestCurrentPricingForm.style.display = 'none';
                }
                else{
                    requestCurrentPricingForm.style.display = 'block';
                    requestCurrentPricingForm.scrollIntoView();
                    window.scrollBy(0, -200); // Scroll 100px up
                }

            }
        },
        navigateToPartSuggestion(){
            this.$router.push({ name: "PartSearchPage", query: { searchTerm: this.details.OemPartCode, activeView: 'SUGGEST_PART'} });
        }
    },
});
